import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { useSettingsStore } from "../stores/settingsStore";
import { useTranslation } from 'react-i18next';

const BannerTwo = () => {
    const { t } = useTranslation();
    const settings = useSettingsStore((state) => state.settings);
    const storeLoading = useSettingsStore((state) => state.loading);
    
    // Local loading state that can be controlled within the component
    const [isLoading, setIsLoading] = useState(true);
    
    // Effect to update local loading state based on store loading state
    useEffect(() => {
        // Set local loading state based on store loading
        if (storeLoading) {
            setIsLoading(true);
        } else {
            // Add a small delay before showing content for smoother transitions
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 300);
            
            return () => clearTimeout(timer);
        }
    }, [storeLoading]);
    
    const sliderSettings = {
        dots: false,
        infinite: true,
        autoplay: true,
        autoplaySpeed: 5000,
        speed: 1000,
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: 0,
    };
    
    // Check if we have banners to display
    const hasBanners = settings.static_banners && settings.static_banners.length > 0;
    
    // Render placeholder for loading state
    const renderPlaceholder = (width = "100%", height = "24px", className = "") => (
        <div 
            className={`bg-gray-100 rounded-8 animate-pulse ${className}`} 
            style={{ width, height }}
        ></div>
    );
    
    // Common style for banner container to maintain consistent dimensions
    const bannerContainerStyle = {
        aspectRatio: '16/9',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        // minHeight: '250px', // Minimum height to ensure visibility on small screens
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
    };
    
    // Determine what content to render based on loading state and data availability
    const renderContent = () => {
        // First priority: Show skeleton loader while loading
        if (isLoading) {
            return (
                // Loading skeleton
                <div 
                    className="banner-item-two" 
                    style={{ 
                        ...bannerContainerStyle,
                        background: '#f3f4f6'
                    }}
                >
                    <div className="tw-h-full tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-p-8">
                        <div className="bg-gray-100 tw-w-full tw-h-full animate-pulse rounded-24"></div>
                        <div className="banner-item-two__content tw-z-10 tw-absolute tw-flex tw-flex-col tw-items-center tw-justify-center">
                            {renderPlaceholder("60%", "24px", "mb-3")}
                            {renderPlaceholder("80%", "48px", "mb-4")}
                            {renderPlaceholder("200px", "48px", "mt-3")}
                        </div>
                    </div>
                </div>
            );
        }
        
        // Second priority: If loaded and has banners, show the slider
        if (hasBanners) {
            return (
                <div className="banner-item-two__slider" style={{ width: '100%' }}>
                    <Slider {...sliderSettings}>
                        {settings.static_banners.map((banner, index) => (
                            <div key={index} style={bannerContainerStyle}>
                                <img 
                                    src={banner} 
                                    alt={`Banner ${index + 1}`} 
                                    style={{ 
                                        objectFit: 'cover', 
                                        width: '100%',
                                        height: '100%'
                                    }}
                                />
                            </div>
                            // <div className="banner-item-two" key={index}>
                            //     <div className="banner-item-two__content">
                            //         <span className="mb-8 text-white h6">{banner.subtitle || t('banner.default_subtitle')}</span>
                            //         <h2 className="text-white banner-item-two__title bounce">
                            //             {banner.title || t('banner.default_title')}
                            //         </h2>
                            //         <Link
                            //             to="/shop"
                            //             className="gap-8 mt-48 btn btn-outline-white d-inline-flex align-items-center rounded-pill"
                            //         >
                            //             {t('banner.shop_now')}
                            //             <span className="text-xl icon d-flex">
                            //                 <i className="ph ph-shopping-cart-simple" />
                            //             </span>
                            //         </Link>
                            //     </div>
                            //     <div className="bottom-0 banner-item-two__thumb position-absolute">
                            //         <img 
                            //             src={banner.image_url} 
                            //             alt={banner.title || t('banner.default_title')} 
                            //             style={{ objectFit: 'cover', aspectRatio: '16/9' }}
                            //         />
                            //     </div>
                            // </div>
                        ))}
                    </Slider>
                </div>
            );
        }
        
        // Third priority: Fallback when no banners available
        return (
            <div 
                className="banner-item-two" 
                style={{ 
                    ...bannerContainerStyle,
                    background: 'linear-gradient(135deg, rgba(25, 25, 25, 0.8) 0%, rgba(42, 42, 68, 0.8) 100%)'
                }}
            >
                <div className="text-center banner-item-two__content">
                    <span className="mb-8 text-white h6">{t('banner.fallback_subtitle')}</span>
                    <h2 className="text-white banner-item-two__title bounce">
                        {t('banner.fallback_title')}
                    </h2>
                    <p className="mt-16 text-white">
                        {t('banner.fallback_message')}
                    </p>
                    <Link
                        to="/shop"
                        className="gap-8 mt-48 btn btn-outline-white d-inline-flex align-items-center rounded-pill"
                    >
                        {t('banner.shop_now')}
                        <span className="text-xl icon d-flex">
                            <i className="ph ph-shopping-cart-simple" />
                        </span>
                    </Link>
                </div>
            </div>
        );
    };
    
    return (
        <div className="banner-two tw-pt-8 xl:tw-pt-16">
            <div className="container container-lg">
                <div className="banner-two-wrapper d-flex align-items-start">
                    <div 
                        className="mb-0 overflow-hidden banner-item-two-wrapper !tw-m-0 rounded-24 position-relative arrow-center flex-grow-1"
                        style={{ 
                            aspectRatio: '16/9',
                        }}
                    >
                        {/* Banner content - skeleton, slider, or fallback */}
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BannerTwo